// Ad-platform match signals, read from the cookies the vendor pixels set on this
// browser and forwarded once, at checkout, so the SERVER-side Conversions API
// purchase can be attributed to the same person as the browser event.
//
// Almost everything here already exists in the document and is simply handed to
// our own API. Two things cannot be: Klaviyo's `kx`, which arrives as a `_kx`
// query param on an emailed link and is gone from the URL long before checkout
// (lib/klaviyokx.ts persists it at landing), and the ad networks' click ids, which
// are gone for exactly the same reason (lib/attribution.ts persists those). Both
// are read back here so the checkout body is still assembled in ONE place.
//
// One signal here is OURS rather than a vendor's: `external_id`, the first-party
// visitor token. It rides along because this object is the checkout body's whole
// `tracking` block, and that block is what the server turns into CAPI user_data.

import { getClickIDs } from "./attribution"
import { getKlaviyoKx } from "./klaviyokx"
import { visitorToken } from "./visitor"

function cookie(name: string): string {
  try {
    const prefix = name + "="
    for (const part of document.cookie.split(";")) {
      const c = part.trim()
      if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length))
    }
  } catch {
    /* cookies disabled */
  }
  return ""
}

export interface MatchSignals {
  fbp: string
  fbc: string
  ttclid: string
  ttp: string
  /** Klaviyo email-link token; "" when the shopper did not arrive from a send. */
  kx: string
  /**
   * The first-party visitor token, forwarded as every ad platform's `external_id`.
   *
   * This field is the whole reason a guest's server-side Purchase could carry any
   * person-identifier at all: the server has ALWAYS read `tracking.external_id`
   * off this body (http/storefront/checkout.go) and hashed it into Meta's
   * `external_id` and Axon's `client_id`, but this object never had the key, so
   * both were dropped by their own omit-empty rules on every order ever placed.
   * Nothing on the server needed changing — the value simply never left here.
   *
   * Sent RAW, not hashed: Meta normalises (lowercase + trim) and hashes
   * `external_id` itself, and capi.hashNorm does exactly the same two steps, so
   * the browser's copy and the server's resolve to one digest. Hashing it here
   * would also cost Axon, which wants `client_id` opaque but intact.
   */
  external_id: string
}

/**
 * Meta sets `_fbc` itself, but only on the landing page that carried `fbclid` —
 * a buyer who navigates before the pixel loads keeps the click id and no cookie.
 * Synthesizing `fb.1.<ms>.<fbclid>` (Meta's documented format) recovers the click
 * attribution in that case.
 *
 * The click ids come from the STORE, not from `location.search`. This function
 * runs on the checkout page, which is several navigations past the landing URL
 * that carried them — so the old `new URLSearchParams(location.search)` read here
 * found an fbclid only for a buyer who paid without ever leaving the page they
 * landed on. Every other Meta-sourced sale reached the Conversions API with no
 * click attribution at all whenever the pixel had been blocked or was slow enough
 * to miss writing `_fbc`.
 */
export function matchSignals(): MatchSignals {
  const ids = getClickIDs()
  let fbc = cookie("_fbc")
  if (!fbc && ids.fbclid) fbc = `fb.1.${Date.now()}.${ids.fbclid}`
  return {
    fbp: cookie("_fbp"),
    fbc,
    ttclid: ids.ttclid || cookie("ttclid"),
    ttp: cookie("_ttp"),
    kx: getKlaviyoKx(),
    // "" for a browser that has not been minted one yet (storage blocked, or the
    // consent gate never opened — ensureVisitor sits behind it). The server's
    // omit-empty then behaves exactly as it does today.
    external_id: visitorToken(),
  }
}
